import { useChat } from '@ai-sdk/react';
import type { UIMessage } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  hydrateEnvelopeMessages,
  readAgentConversation,
} from './agentConversationsApi';
import {
  AgentLiveReplyLostError,
  AgentTurnNotSentError,
  QueuedConversationTransport,
} from './queuedConversationTransport';
import type { SendAgentConversationMessageBody } from './types';

interface UseCommendaAiOptions {
  /** Fired when a send returns 401 (session expired mid-conversation). */
  onUnauthorized?: () => void;
  /** Fields every send carries, such as the builder's target version. */
  buildSendBody?: () => Partial<SendAgentConversationMessageBody>;
}

/** Chat reads its reply live from the send; every other surface posts and polls the conversation read.
 * Either way the turn runs detached server-side and writes its own messages, so a live reply that
 * breaks off is picked back up from the read. */
export function useCommendaAiChat(
  conversationId: string,
  initialMessages: UIMessage[],
  { onUnauthorized, buildSendBody }: UseCommendaAiOptions = {}
) {
  // The send in flight, settled when the server takes the turn or by the error saying it never did.
  const pendingSendRef = useRef<{
    resolve: () => void;
    reject: (error: Error) => void;
  } | null>(null);
  const settlePendingSend = useCallback((error: Error | null) => {
    const pending = pendingSendRef.current;
    pendingSendRef.current = null;
    if (error) pending?.reject(error);
    else pending?.resolve();
  }, []);
  const handleTurnSent = useCallback(
    () => settlePendingSend(null),
    [settlePendingSend]
  );

  const transport = useMemo(
    () =>
      new QueuedConversationTransport(conversationId, {
        ...(buildSendBody ? { buildSendBody } : {}),
        ...(onUnauthorized ? { onUnauthorized } : {}),
        onTurnSent: handleTurnSent,
      }),
    [conversationId, onUnauthorized, buildSendBody, handleTurnSent]
  );

  const chat = useChat<UIMessage>({
    id: conversationId,
    messages: initialMessages,
    transport,
    onError: (error) => {
      if (error instanceof AgentTurnNotSentError) settlePendingSend(error);
    },
  });
  const {
    stop: stopChat,
    sendMessage,
    setMessages,
    resumeStream,
    error: chatError,
    status,
  } = chat;

  const [hasLiveReplyRecoveryFailed, setHasLiveReplyRecoveryFailed] =
    useState(false);
  const isRecoveringLiveReply =
    chatError instanceof AgentLiveReplyLostError && !hasLiveReplyRecoveryFailed;
  useEffect(() => {
    if (!(chatError instanceof AgentLiveReplyLostError)) return;

    setHasLiveReplyRecoveryFailed(false);
    readAgentConversation(conversationId)
      .then((envelope) => {
        setMessages(hydrateEnvelopeMessages(envelope));
        return resumeStream();
      })
      .catch(() => setHasLiveReplyRecoveryFailed(true));
  }, [chatError, conversationId, setMessages, resumeStream]);

  /** `sendMessage` resolves only once the reply is done and never rejects. This resolves as soon as
   * the server holds the turn and rejects when it never got it, so a composer can keep what was typed
   * exactly when the turn was lost. A failure after that surfaces through `error`. */
  const sendTurn = useCallback(
    (...args: Parameters<typeof sendMessage>) =>
      new Promise<void>((resolve, reject) => {
        pendingSendRef.current = { resolve, reject };
        sendMessage(...args).then(
          () => settlePendingSend(null),
          (error: Error) => settlePendingSend(error)
        );
      }),
    [sendMessage, settlePendingSend]
  );

  // A resumed reply, which every turn sent from a start screen becomes, is only stoppable through the transport.
  const stop = useCallback(async () => {
    transport.stopResumedReply();
    await stopChat();
  }, [transport, stopChat]);

  // A reply being recovered is still pending, not failed.
  return {
    ...chat,
    status: isRecoveringLiveReply ? 'submitted' : status,
    error: isRecoveringLiveReply ? undefined : chatError,
    stop,
    sendTurn,
  };
}
